import { useAuthenticatedUser } from "@/queries/user-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { ErrorMessage } from "@/components/ErrorMessage";
import { IconPlusSquare } from "@/assets/icons/geist/IconPlusSquare";
import { Switch } from "@/components/Switch";
import { Heading3 } from "@/components/podkit/typography/Headings";
import {
    useListIntegrationDefinitions,
    useListIntegrations,
    useCreateIntegration,
    useUpdateIntegration,
} from "@/queries/integration-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Integration, IntegrationDefinition } from "gitpod-next-api/gitpod/v1/integration_pb";
import type { FC } from "react";
import { useState, useCallback } from "react";

type IntegrationWithDefinition = {
    integration: PlainMessage<Integration> | null;
    definition: PlainMessage<IntegrationDefinition>;
};

export const AgentIntegrations: FC = () => {
    const { data: user } = useAuthenticatedUser();
    const { toast } = useToast();

    const {
        data: definitions,
        isLoading: isLoadingDefinitions,
        error: definitionsError,
    } = useListIntegrationDefinitions();

    const {
        data: integrations,
        isLoading: isLoadingIntegrations,
        error: integrationsError,
    } = useListIntegrations(user?.organizationId);

    const createIntegrationMutation = useCreateIntegration();
    const updateIntegrationMutation = useUpdateIntegration();

    const [creatingIntegrationId, setCreatingIntegrationId] = useState<string | null>(null);

    const isLoading = isLoadingDefinitions || isLoadingIntegrations;
    const error = definitionsError || integrationsError;

    const integrationsMap = new Map<string, PlainMessage<Integration>>();
    integrations?.forEach((integration) => {
        integrationsMap.set(integration.integrationDefinitionId, integration);
    });

    const integrationsWithStatus =
        definitions?.map((definition) => {
            const integration = integrationsMap.get(definition.id);
            return {
                definition,
                integration: integration || null,
            };
        }) || [];

    const handleCreateIntegration = useCallback(
        async (definition: PlainMessage<IntegrationDefinition>) => {
            if (!user?.organizationId) return;

            setCreatingIntegrationId(definition.id);

            try {
                await createIntegrationMutation.mutateAsync({
                    integrationDefinitionId: definition.id,
                    enabled: true,
                    runnerId: "",
                    host: "",
                });
                toast({
                    title: "Integration created successfully",
                    description: `${definition.name} integration has been created and enabled.`,
                });
            } catch (error) {
                toast({
                    title: "Failed to create integration",
                    description: formatError(error),
                });
            } finally {
                setCreatingIntegrationId(null);
            }
        },
        [user?.organizationId, createIntegrationMutation, toast],
    );

    const handleToggleEnabled = useCallback(
        async (integration: PlainMessage<Integration>) => {
            try {
                await updateIntegrationMutation.mutateAsync({
                    id: integration.id,
                    enabled: !integration.enabled,
                });
                toast({
                    title: "Integration updated successfully",
                    description: `Integration has been ${!integration.enabled ? "enabled" : "disabled"}.`,
                });
            } catch (error) {
                toast({
                    title: "Failed to update integration",
                    description: formatError(error),
                });
            }
        },
        [updateIntegrationMutation, toast],
    );

    return (
        <div className="flex flex-col gap-4">
            <Heading3>Integrations</Heading3>
            <SkeletonBlock ready={!isLoading} className="min-h-[200px]">
                {error && (
                    <div className="flex flex-col gap-2">
                        <Text className="text-lg font-bold text-content-negative">Failed to load integrations</Text>
                        <ErrorMessage error={error} />
                    </div>
                )}

                {!error && (
                    <div className="flex flex-col gap-4">
                        <Text className="text-base text-content-secondary">
                            Manage integrations available to your organization members.
                        </Text>
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                            {integrationsWithStatus.map(({ definition, integration }: IntegrationWithDefinition) => (
                                <IntegrationCard
                                    key={definition.id}
                                    definition={definition}
                                    integration={integration}
                                    onCreateIntegration={() => handleCreateIntegration(definition)}
                                    onToggleEnabled={() => integration && handleToggleEnabled(integration)}
                                    isCreating={creatingIntegrationId === definition.id}
                                    isUpdating={updateIntegrationMutation.isPending}
                                />
                            ))}
                        </div>

                        {integrationsWithStatus.length === 0 && !isLoading && (
                            <div className="flex flex-col items-center gap-4 py-8">
                                <Text className="text-lg font-bold text-content-secondary">
                                    No integration definitions available
                                </Text>
                                <Text className="text-base text-content-secondary">
                                    Integration definitions will appear here when available.
                                </Text>
                            </div>
                        )}
                    </div>
                )}
            </SkeletonBlock>
        </div>
    );
};

type IntegrationCardProps = {
    definition: PlainMessage<IntegrationDefinition>;
    integration: PlainMessage<Integration> | null;
    onCreateIntegration: () => void;
    onToggleEnabled: () => void;
    isCreating: boolean;
    isUpdating: boolean;
};

const IntegrationCard: FC<IntegrationCardProps> = ({
    definition,
    integration,
    onCreateIntegration,
    onToggleEnabled,
    isCreating,
    isUpdating,
}) => {
    const hasIntegration = !!integration;

    return (
        <div className="group flex flex-col gap-4 rounded-xl border-0.5 border-solid border-border-base bg-surface-glass px-5 py-3 transition-all duration-200 hover:border-border-medium hover:shadow-md">
            <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                    {definition.iconUrl && (
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg">
                            <img src={definition.iconUrl} alt={`${definition.name} icon`} className="h-8 w-8" />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <h3 className="truncate text-lg font-semibold leading-tight text-content-primary">
                            {definition.name}
                        </h3>
                        {definition.description && (
                            <p className="line-clamp-2 text-sm text-content-secondary">{definition.description}</p>
                        )}
                    </div>
                </div>
                {hasIntegration && (
                    <Switch
                        state={integration.enabled ? "checked" : "unchecked"}
                        onToggle={() => onToggleEnabled()}
                        id={`toggle-${definition.id}`}
                        disabled={isUpdating}
                    />
                )}
            </div>

            {!hasIntegration && (
                <div className="mt-auto flex items-center justify-end gap-2">
                    <Button
                        variant="primary"
                        onClick={onCreateIntegration}
                        disabled={isCreating}
                        LeadingIcon={IconPlusSquare}
                        data-testid={`create-${definition.id}`}
                        data-tracking-id="oncreateintegration"
                    >
                        {isCreating ? "Creating..." : "Create"}
                    </Button>
                </div>
            )}
        </div>
    );
};
